// A walkthrough step's click and fill (design/2026-10-20-walkthroughs.md):
// for the pages that register no page tool for a control, the runner presses
// or types into it by its on-screen label, found the way the outline finds
// it (help/outline findControl: role and accessible name, never inside the
// card or the Help pane).
//
// A click whose label commits something runs nothing (help/walkthroughGuard),
// checked against the step's label and the label of the control it found, so
// a loose label ("Table") cannot land on "Save Table". A switch is never
// clicked, whatever its label: every Platinum switch writes a setting. Nor is
// a form's submit button: a dispatched click submits the form, whatever the
// button says ("Add", "OK", "Create"). A fill
// types only into a text field and never presses Enter.

import { controlLabel, controlRole } from '../voice/screenControls'
import { findControl } from './outline'
import type { TourActionResult } from './tourActions'
import type { TourStep } from './tourRuntime'
import { clickIsSafe } from './walkthroughGuard'

const notOnScreen = (label: string) => `no control labelled "${label}" is on screen yet`

/** The refusal the card shows for a commit label. */
export function refusedClick(label: string): string {
  return `a walkthrough does not click "${label}" for you; do it yourself`
}

function press(el: Element) {
  // MUI Select and Menu open on mousedown, a Button on click: send all three.
  for (const type of ['mousedown', 'mouseup', 'click']) {
    el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, button: 0 }))
  }
}

function type(el: HTMLInputElement | HTMLTextAreaElement, text: string) {
  // React tracks a controlled input's value and ignores a plain `.value =`:
  // set it through the element's own prototype setter, then say it changed.
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, text)
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

/** A submit button of a form, including a `<button>` with no type inside one. */
function submitsAForm(el: Element): boolean {
  return (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) && (el.type === 'submit' || el.type === 'image') && el.form !== null
}

/** Run a step's click or fill against the page on screen. Never throws. */
export async function runDomAction(step: Pick<TourStep, 'click' | 'fill'>, root: ParentNode = document): Promise<TourActionResult> {
  if (step.click) {
    const asked = clickIsSafe(step.click)
    if (!asked.ok) return { error: refusedClick(step.click), retry: false }
    const el = findControl(step.click, root)
    if (!el) return { error: notOnScreen(step.click), retry: true }
    const label = controlLabel(el) || step.click
    if (!clickIsSafe(label).ok || controlRole(el) === 'switch' || submitsAForm(el)) return { error: refusedClick(label), retry: false }
    press(el)
    return { did: `Clicked ${label}`, picked: {} }
  }
  if (step.fill) {
    const { control, text } = step.fill
    const el = findControl(control, root)
    if (!el) return { error: notOnScreen(control), retry: true }
    const label = controlLabel(el) || control
    const field = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
    if (!field || controlRole(el) !== 'textbox' || el.readOnly || el.disabled) {
      return { error: `"${label}" is not a field a walkthrough can type into`, retry: false }
    }
    type(el, text)
    return { did: `Typed into ${label}`, picked: {} }
  }
  return { error: 'the step has nothing to click or type', retry: false }
}
